import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { Palette, Box, Flex, Text } from '@updater/ui-uds';
import { isTaskCompletedOrValidating } from 'flows/core/utils/card-makers/utils';
import { InitiatePayload } from 'flows/core/types/cards';
import { useDaysLeft } from './utils';

const Card = styled(Box)`
  background-color: ${Palette.mint30};
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  min-height: 6.125rem;
`;

const BarContainer = styled(Box)`
  width: 100%;
  height: 0.5rem;
  background-color: ${Palette.mint10};
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

const BarFill = styled(Box)<{ widthPct: number }>`
  width: ${({ widthPct }) => widthPct}%;
  height: 100%;
  background-color: ${Palette.green};
  border-radius: 0.25rem;
`;

export interface ProgressBarCardProps {
  /** Priority and required todo cards */
  priorityCards: InitiatePayload[];
  /** Completed cards */
  completedCards: InitiatePayload[];
  /** Move date in YYYY-MM-DD format */
  currentMoveDate: string | null | undefined;
}

export const ProgressBarCard: FC<ProgressBarCardProps> = ({
  priorityCards,
  completedCards,
  currentMoveDate,
}) => {
  // Calculate days left until move
  const daysLeft = useDaysLeft(currentMoveDate);

  // Calculate completed required tasks
  const completedRequiredTasks = useMemo(() => {
    if (!completedCards) return [];
    return completedCards.filter((card: InitiatePayload) =>
      isTaskCompletedOrValidating(card.data) && (card.data?.required || card.data?.priority)
    );
  }, [completedCards]);

  // Calculate totals for progress bar
  const totalTasks = (priorityCards?.length || 0) + completedRequiredTasks.length;
  const completedTasks = (priorityCards?.filter((card: InitiatePayload) => isTaskCompletedOrValidating(card.data)).length || 0) + completedRequiredTasks.length;

  const showBar = totalTasks > 0 && completedTasks > 0;

  const getDaysText = (): string => {
    if (daysLeft === 0) {
      return "It's move-in day!";
    } else if (daysLeft === 1) {
      return "Tomorrow's the big day!";
    } else if (daysLeft < 0) {
      return "Welcome home!";
    } else {
      return `${daysLeft} days left until your move`;
    }
  };

  // INITIAL "days-left" variant
  if (!showBar) {
    return (
      <Card data-testid="progress-bar-card">
        <Flex flexDirection="column" justifyContent="space-between" minHeight="4rem">
          <Text variant="lBold" as="h2">
            {getDaysText()}
          </Text>
          <Text variant="s" as="h2">
            Finish your first task!
          </Text>
        </Flex>
      </Card>
    );
  }

  // PROGRESS-BAR variant
  const pct = Math.round((completedTasks / totalTasks) * 100);
  const remainingTasks = totalTasks - completedTasks;
  let title: string;
  if (pct === 100) title = "You're all set";
  else if (remainingTasks === 1) title = 'Just one more!';
  else if (pct === 50) title = "You're halfway there!";
  else if (pct > 50) title = "Over halfway there!";
  else title = 'Moving progress';

  return (
    <Card data-testid="progress-bar-card">
      <Flex alignItems="center" justifyContent="space-between">
        <Text variant="lBold">
          {title}
        </Text>
        <Text variant="lBold">
          {pct}%
        </Text>
      </Flex>

      <BarContainer>
        <BarFill widthPct={pct} />
      </BarContainer>

      <Flex justifyContent="space-between" flexWrap="wrap" gap="0.25rem">
        <Text variant="s">
          {completedTasks} of {totalTasks} tasks done
        </Text>
        <Text variant="s">
          {getDaysText()}
        </Text>
      </Flex>
    </Card>
  );
};
